/**
 * BrigadeSection — Зөвшөөрлийн §6 «БРИГАДЫН БҮРЭЛДЭХҮҮН, ЗААВАРЧИЛГАА».
 *
 * HRT-ийн `_step6` (AT_Step6)-ийн орлуулга.
 *
 * ⚠ БҮРЭЛДЭХҮҮНИЙГ ЭНД НЭМЭХГҮЙ. Бригад нь ажилтан өөрөө ӨЭА-д гарахаар
 *   бүртгүүлэхэд (`/highrisk/registerTn` → `oea_worker_registrations`)
 *   үүсдэг. Энэ хэсэг нь тэр жагсаалт дээр «ХЭН зааварчилсан» гэдгийг
 *   нэмнэ — нэг хүн хоёр газар бүртгэгдэхээс сэргийлэв.
 *
 * ⚠ HRT-ийн `Instr` талбар нь ЗААВРЫН ДУГААР БИШ: хуучин дэлгэц дээр
 *   «Зааварчилгаа өгсөн ажил гүйцэтгэгч» баганад харагддаг ХҮН байсан.
 *   Тиймээс сонголт нь §4.3-д бүртгэсэн «Ажил гүйцэтгэгч»-үүдээс гарна.
 *
 * Backend: /xab/hr-job-brigade (GET · PUT)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, Select, Tag, Tooltip, message } from "antd";
import { getJobBrigade, setJobBriefing } from "src/api/high_risk";

export default function BrigadeSection({ jobId, readonly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyTn, setBusyTn] = useState("");

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      setData(await getJobBrigade(jobId));
    } catch (ex) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  const members = data?.members || [];
  const performers = data?.performers || [];

  const setBriefer = async (tn, briefer) => {
    setBusyTn(tn);
    try {
      const p = performers.find((x) => String(x.tn) === String(briefer));
      await setJobBriefing({
        job_id: jobId,
        worker_tn: tn,
        briefer_tn: briefer || "",
        briefer_name: p?.name || "",
      });
      load();
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Хадгалж чадсангүй.");
    } finally {
      setBusyTn("");
    }
  };

  if (!jobId) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
        <div className="font-semibold text-slate-700 mb-1">
          <i className="fas fa-users mr-1" />
          Бригадын бүрэлдэхүүн (§6)
        </div>
        <div style={{ fontSize: 12, color: "#8A94A6" }}>
          Нарядаа эхлээд хадгална уу.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-2 mb-2">
      <div className="font-semibold text-slate-700 mb-2 flex items-center justify-between">
        <span>
          <i className="fas fa-users mr-1" />
          Бригадын бүрэлдэхүүн, зааварчилгаа (§6)
          <span style={{ fontWeight: 400, fontSize: 12, color: "#8A94A6" }}>
            {" "}
            · {members.length} хүн · зааварчилсан {data?.briefed_count || 0}
          </span>
        </span>
        {loading ? (
          <span style={{ fontSize: 12, color: "#8A94A6" }}>ачааллаж…</span>
        ) : (
          <Button size="small" type="link" onClick={load}>
            <i className="fas fa-sync" style={{ marginRight: 4 }} />
            Шинэчлэх
          </Button>
        )}
      </div>

      {data?.unavailable ? (
        <div
          style={{
            fontSize: 12,
            color: "#92400E",
            background: "#FEF3C7",
            padding: "6px 8px",
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          Хүснэгт бэлэн болоогүй байна — <code>create_hr_job_briefing.sql</code>{" "}
          миграцыг ажиллуулна уу.
        </div>
      ) : null}

      {members.length === 0 ? (
        /* ⚠ Урьд нь «хаанаас нэмэх вэ» гэдгийг ТЕКСТЭЭР л хэлдэг байсан
           тул хэрэглэгч хуудсаа хайж тэнэдэг байв. Бригад энд НЭМЭГДДЭГГҮЙ
           (нэг хүн хоёр газар бүртгэгдэхээс сэргийлсэн) тул зөв хуудас руу
           шууд аваачих нь ганц зөв шийдэл. Нарядын дугаарыг дамжуулснаар
           тэнд ажлын сонголт нь бэлэн ирнэ. */
        <div style={{ fontSize: 12, color: "#8A94A6" }}>
          <div style={{ color: "#B0B7C3" }}>
            Ажилтан хараахан бүртгэгдээгүй. Бригадын гишүүд «ӨЭА-д гарах
            ажилтны бүртгэл» хуудсаар энэ нарядад холбогдоно.
          </div>
          <Button
            size="small"
            type="primary"
            ghost
            style={{ marginTop: 6 }}
            onClick={() =>
              window.open(`/highrisk/registerTn?job=${jobId}`, "_blank")
            }
          >
            <i className="fas fa-user-plus" style={{ marginRight: 4 }} />
            Ажилтан бүртгэх хуудас нээх (№{jobId})
          </Button>
        </div>
      ) : (
        <div style={{ maxHeight: 260, overflowY: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Б/Д", "Овог нэр", "Албан тушаал", "Зааварчилсан"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "4px 6px",
                      borderBottom: "1px solid #E3E7EC",
                      fontSize: 10.5,
                      color: "#7C8796",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.tn} style={{ borderTop: "1px solid #F0F2F5" }}>
                  <td style={{ padding: "4px 6px" }}>
                    <Tag color="blue" style={{ marginRight: 0 }}>
                      {m.tn}
                    </Tag>
                  </td>
                  <td style={{ padding: "4px 6px", fontWeight: 600 }}>
                    {m.name || "—"}
                    {/* 15А шалгалт бөглөсөн эсэх — ӨЭА-д гарах нөхцөл */}
                    {m.checklist_15_completed ? (
                      <Tooltip title="15 критик асуумжаа бөглөсөн">
                        <i
                          className="fas fa-check-circle"
                          style={{ color: "#16A34A", marginLeft: 5, fontSize: 11 }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="15 критик асуумжаа бөглөөгүй">
                        <i
                          className="fas fa-exclamation-circle"
                          style={{ color: "#D97706", marginLeft: 5, fontSize: 11 }}
                        />
                      </Tooltip>
                    )}
                  </td>
                  <td style={{ padding: "4px 6px", color: "#8A94A6" }}>
                    {m.position || ""}
                  </td>
                  <td style={{ padding: "4px 6px", width: 210 }}>
                    <Select
                      size="small"
                      allowClear
                      style={{ width: "100%" }}
                      placeholder={
                        performers.length
                          ? "Ажил гүйцэтгэгч сонгох"
                          : "§4.3-д ажил гүйцэтгэгч нэмнэ үү"
                      }
                      disabled={readonly || !performers.length}
                      loading={busyTn === m.tn}
                      value={m.briefer_tn || undefined}
                      onChange={(v) => setBriefer(m.tn, v)}
                      options={performers.map((p) => ({
                        value: p.tn,
                        label: p.name || p.tn,
                      }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {members.length && !performers.length ? (
        <div style={{ fontSize: 11, color: "#B45309", marginTop: 6 }}>
          Зааварчлагчийг §4.3 «Ажил гүйцэтгэгч»-ээс сонгоно — эхлээд тэнд
          хүн нэмнэ үү.
        </div>
      ) : null}
    </div>
  );
}
